<template>
  <div class="suppliers">
    <Actions :items="actionsHeader" :right="true" />
    <MobileTable
      v-model:selection="store.state.supplier.selectedSuppliers"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <DesktopTable
      v-model:selection="store.state.supplier.selectedSuppliers"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <p v-if="showEmptyState" class="empty-note">No hay proveedores para mostrar.</p>
    <Actions :items="actionsFooter" />
  </div>
</template>

<script>
import { computed, reactive, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import { openMode, actions } from "../../constants";

export default {
  components: {
    Actions,
    MobileTable,
    DesktopTable,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const tableData = computed(() =>
      (store.state.supplier.suppliers || []).map((supplier) => ({
        id: supplier.id,
        taxId: supplier.taxId || "",
        name: supplier.name || "",
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || "",
        website: supplier.website || "",
        comment: supplier.comment || "",
      }))
    );

    const listLabel = computed(() => `${route.name || "Proveedores"} (${tableData.value.length})`);

    const columns = reactive([
      { field: "taxId", header: "RUT / CI", sort: true },
      { field: "name", header: "Nombre", sort: true },
      { field: "email", header: "Correo", sort: true },
      { field: "phone", header: "Telefono", sort: true },
      { field: "address", header: "Direccion", sort: true },
      { field: "website", header: "Sitio web", sort: true },
      { field: "comment", header: "Comentario" },
    ]);

    const showEmptyState = computed(
      () => !store.state.loading && Boolean(buildingId.value) && !tableData.value.length
    );

    const notify = (type, message) => {
      if (!message) return;
      store.commit("setToast", { type, message });
    };

    const ensureBreadcrumb = () => {
      const breadcrumbs = store.state.general.breadcrumbs;
      const hasCurrent = breadcrumbs.some((item) => item.to === route.path);
      if (!breadcrumbs.length) {
        store.commit("setBreadcrumbs", [
          { label: "Inicio", to: "/home" },
          { label: "Contabilidad", to: "/contabilidad" },
          { label: route.name, to: route.path },
        ]);
        return;
      }
      const hasContabilidad = breadcrumbs.some((item) => item.to === "/contabilidad");
      if (!hasContabilidad) {
        store.commit("setBreadcrumbs", [
          { label: "Inicio", to: "/home" },
          { label: "Contabilidad", to: "/contabilidad" },
          { label: route.name, to: route.path },
        ]);
        return;
      }
      if (!hasCurrent) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const loadSuppliers = async () => {
      if (!buildingId.value) return;
      const response = await store.dispatch(actions.supplierActions.findAll, {
        buildingId: buildingId.value,
      });
      if (!response?.ok && response?.message) {
        notify("error", response.message);
      }
    };

    const requireSelection = (single = false) => {
      const selection = store.state.supplier.selectedSuppliers;
      if (!selection.length) {
        notify("warn", "Seleccione al menos un proveedor");
        return false;
      }
      if (single && selection.length !== 1) {
        notify("warn", "Seleccione un unico proveedor");
        return false;
      }
      return true;
    };

    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/contabilidad/proveedor/nuevo");
    };

    const onModify = () => {
      if (!requireSelection(true)) return;
      store.commit("setOpenMode", openMode.MODIFY);
      router.push("/contabilidad/proveedor/modificar");
    };

    const onDelete = async () => {
      if (!requireSelection()) return;
      if (!buildingId.value) {
        notify("error", "Seleccione un edificio para continuar");
        return;
      }
      const confirmed = window.confirm("Eliminar los proveedores seleccionados?");
      if (!confirmed) return;

      store.commit("setLoading", true);
      try {
        const responses = await Promise.all(
          store.state.supplier.selectedSuppliers.map((supplier) =>
            store.dispatch(actions.supplierActions.delete, {
              id: supplier.id,
              buildingId: buildingId.value,
            })
          )
        );
        const hasError = responses.some((response) => !response?.ok);
        if (hasError) {
          notify("error", "No fue posible eliminar algunos proveedores.");
        } else {
          notify("success", "Proveedores eliminados correctamente.");
          await loadSuppliers();
        }
      } finally {
        store.commit("setLoading", false);
        store.commit("setSelectedSuppliers", []);
      }
    };

    const onDownloadExcel = async () => {
      if (!tableData.value.length) {
        notify("warn", "No hay datos para exportar");
        return;
      }
      store.commit("setLoading", true);
      try {
        const request = {
          fileName: "proveedores",
          headers: columns.map((column) => column.header),
          data: tableData.value.map((row) => [
            row.taxId,
            row.name,
            row.email,
            row.phone,
            row.address,
            row.website,
            row.comment,
          ]),
        };
        await store.dispatch(actions.supplierActions.downloadExcel, request);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const actionsHeader = reactive([
      {
        name: "Descargar Excel",
        action: onDownloadExcel,
        hidden: true,
      },
    ]);

    const actionsFooter = reactive([
      {
        name: "Nuevo",
        action: onCreate,
        hidden: router.getRoutes().find((item) => item.path === "/contabilidad/proveedor/nuevo"),
      },
      {
        name: "Modificar",
        action: onModify,
        hidden: router.getRoutes().find((item) => item.path === "/contabilidad/proveedor/modificar"),
      },
      {
        name: "Eliminar",
        action: onDelete,
        hidden: true,
      },
    ]);

    onMounted(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedSuppliers", []);
      ensureBreadcrumb();
      try {
        await loadSuppliers();
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        store.commit("setSelectedSuppliers", []);
        try {
          await loadSuppliers();
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    watch(
      () => route.path,
      (newPath, oldPath) => {
        if (
          oldPath === "/contabilidad/proveedor/listado" &&
          newPath &&
          !newPath.startsWith("/contabilidad/proveedor/")
        ) {
          store.commit("removeBreadcrumb");
        }
      }
    );

    return {
      store,
      columns,
      tableData,
      actionsHeader,
      actionsFooter,
      listLabel,
      showEmptyState,
    };
  },
};
</script>

<style scoped>
.suppliers {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
}

.empty-note {
  margin: 1rem 0;
  text-align: center;
  color: #64748b;
}
</style>
